import type { MaybeRefOrGetter } from 'vue';
import { useTimeoutFn } from '@vueuse/core';
import { computed, nextTick, readonly, ref, shallowRef, toValue, watch } from 'vue';

const COLLAPSE_DISTANCE = 72;
const EXPAND_DISTANCE = 64;
const FLICK_DISTANCE = 32;
const FLICK_VELOCITY = 0.5;
const MAX_FLICK_SAMPLE_AGE = 80;
const AXIS_LOCK_DISTANCE = 8;
const MIN_SETTLE_DURATION = 60;
const MIN_SETTLE_DISTANCE_RATIO = 0.35;

interface UseOnboardingPopupDragOptions {
	/** Header region whose measured height is the collapsed drag target. */
	collapsedElement: MaybeRefOrGetter<HTMLElement | null | undefined>;
	collapsed: MaybeRefOrGetter<boolean>;
	/** Base settle duration in milliseconds; also controls when the temporary height is released. */
	duration: number;
	/** Enables new gestures. Disabling resets an active drag, but lets an existing settle finish. */
	enabled: MaybeRefOrGetter<boolean>;
	/** Requests the parent-owned collapsed state; callback promises are not awaited. */
	onCollapse: () => void;
	/** Must reveal the expanded content by the next Vue tick so its height can be measured. */
	onExpand: () => void;
	/** Measured popup container; bind the returned `style` here. */
	surfaceElement: MaybeRefOrGetter<HTMLElement | null | undefined>;
}

interface DragGesture {
	pointerId: number;
	axis: 'horizontal' | 'vertical' | null;
	startX: number;
	startY: number;
	lastY: number;
	lastTime: number;
	distance: number;
	velocity: number;
	opening: boolean;
	startedOnInteractiveElement: boolean;
}

/**
 * Previews and settles the mobile guide's height during upward expansion and downward collapse.
 * The parent owns the collapsed state; this composable requests changes through callbacks and
 * releases its temporary height after settling. Distance or a recent flick can complete a gesture.
 *
 * Bind `events.start`, `move`, `end`, and `cancel` to the drag region's pointer events, and
 * `captureClick` to its capture-phase click event to prevent a drag from activating a child control.
 * Bind `clickCollapsed` separately to the collapsed button so taps and keyboard activation still work.
 * Apply `style` to `surfaceElement`; use the read-only `dragging` and `settling` flags to select
 * immediate height updates versus CSS transitions. Create this within the component's effect scope.
 */
export function useOnboardingPopupDrag(options: UseOnboardingPopupDragOptions) {
	const activeGesture = shallowRef<DragGesture | null>(null);
	const dragging = computed(() => activeGesture.value !== null);
	const settling = ref(false);
	const height = ref<number | null>(null);
	const settleDuration = ref(options.duration);
	// Settling still needs the measured sizes, and the released pointer can produce a click.
	let collapsedHeight = 0;
	let expandedHeight = 0;
	let suppressNextClick = false;

	const style = computed(() => ({
		height: height.value === null ? undefined : `${height.value}px`,
		transitionDuration: settling.value ? `${settleDuration.value}ms` : undefined,
	}));
	const { start: clearSettledHeight, stop: cancelSettledHeight } = useTimeoutFn(() => {
		settling.value = false;
		height.value = null;
	}, options.duration, { immediate: false });

	watch(() => toValue(options.enabled), (enabled) => {
		if (!enabled && !settling.value) reset();
	});

	function start(event: PointerEvent) {
		if (!toValue(options.enabled) || event.isPrimary === false || event.button !== 0) return;
		const gesture: DragGesture = {
			opening: toValue(options.collapsed),
			startedOnInteractiveElement: Boolean(
				(event.target as Element | null)?.closest?.('button, a, input, select, textarea, [role="button"]'),
			),
			pointerId: event.pointerId,
			axis: null,
			startX: event.clientX,
			startY: event.clientY,
			lastY: event.clientY,
			lastTime: event.timeStamp,
			distance: 0,
			velocity: 0,
		};

		suppressNextClick = false;

		({ collapsedHeight, expandedHeight } = getDragHeights(
			toValue(options.surfaceElement),
			toValue(options.collapsedElement),
		));

		// The collapsed surface includes padding outside the header region.
		if (gesture.opening) collapsedHeight = expandedHeight;

		cancelSettledHeight();
		settling.value = false;
		activeGesture.value = gesture;
		height.value = (gesture.opening ? collapsedHeight : expandedHeight) || null;

		// Preserve a button's click target until movement establishes a drag.
		if (!gesture.startedOnInteractiveElement) {
			(event.currentTarget as HTMLElement | null)?.setPointerCapture?.(event.pointerId);
		}
	}

	function move(event: PointerEvent) {
		const gesture = getActiveGesture(event);
		if (!gesture) return;

		const distanceX = event.clientX - gesture.startX;
		const distanceY = event.clientY - gesture.startY;

		if (!gesture.axis && Math.max(Math.abs(distanceX), Math.abs(distanceY)) >= AXIS_LOCK_DISTANCE) {
			gesture.axis = Math.abs(distanceY) >= Math.abs(distanceX) ? 'vertical' : 'horizontal';

			if (gesture.startedOnInteractiveElement) {
				(event.currentTarget as HTMLElement | null)?.setPointerCapture?.(event.pointerId);
			}
		}

		if (gesture.axis === 'horizontal') {
			suppressNextClick = gesture.startedOnInteractiveElement;
			reset();
			return;
		}

		gesture.distance = Math.max(0, gesture.opening ? -distanceY : distanceY);
		const directionalDelta = gesture.opening ? gesture.lastY - event.clientY : event.clientY - gesture.lastY;
		gesture.velocity = Math.max(0, directionalDelta) / Math.max(event.timeStamp - gesture.lastTime, 1);
		gesture.lastY = event.clientY;
		gesture.lastTime = event.timeStamp;

		if (gesture.opening) {
			height.value = collapsedHeight + gesture.distance;
		}
		else if (expandedHeight > 0) {
			height.value = Math.max(collapsedHeight, expandedHeight - gesture.distance);
		}

		if (gesture.distance > 0 && event.cancelable) event.preventDefault();
	}

	async function end(event: PointerEvent) {
		const gesture = getActiveGesture(event);
		if (!gesture) return;

		const target = event.currentTarget as HTMLElement | null;
		if (target?.hasPointerCapture?.(event.pointerId)) target.releasePointerCapture(event.pointerId);
		const releaseVelocity = event.timeStamp - gesture.lastTime <= MAX_FLICK_SAMPLE_AGE ? gesture.velocity : 0;

		if (gesture.axis === null) {
			reset();
			return;
		}

		if (gesture.opening) {
			suppressNextClick = true;
			const shouldComplete = gesture.axis === 'vertical'
				&& (gesture.distance >= EXPAND_DISTANCE || (gesture.distance >= FLICK_DISTANCE && releaseVelocity >= FLICK_VELOCITY));

			if (!shouldComplete) {
				settle(collapsedHeight);
				return;
			}

			const previewHeight = height.value ?? collapsedHeight;
			options.onExpand();
			await nextTick();

			const surface = toValue(options.surfaceElement);
			const targetHeight = Math.max(
				expandedHeight,
				previewHeight,
				surface?.scrollHeight ?? 0,
			);

			settle(targetHeight, releaseVelocity);
			return;
		}

		suppressNextClick = gesture.startedOnInteractiveElement && gesture.axis !== null;
		const shouldComplete = gesture.axis !== 'horizontal'
			&& (gesture.distance >= COLLAPSE_DISTANCE || (gesture.distance >= FLICK_DISTANCE && releaseVelocity >= FLICK_VELOCITY));

		settle(shouldComplete ? collapsedHeight : expandedHeight, shouldComplete ? releaseVelocity : 0);
		if (shouldComplete) options.onCollapse();
	}

	function cancel(event: PointerEvent) {
		const gesture = getActiveGesture(event);
		if (!gesture) return;

		settle(gesture.opening ? collapsedHeight : expandedHeight);
	}

	function clickCollapsed() {
		options.onExpand();
	}

	function captureClick(event: MouseEvent) {
		if (event.detail === 0 || !suppressNextClick) return;

		suppressNextClick = false;
		event.preventDefault();
		event.stopPropagation();
	}

	function getActiveGesture(event: PointerEvent) {
		const gesture = activeGesture.value;
		return gesture?.pointerId === event.pointerId ? gesture : null;
	}

	function reset() {
		cancelSettledHeight();
		activeGesture.value = null;
		settling.value = false;
		height.value = null;
		collapsedHeight = 0;
		expandedHeight = 0;
	}

	function settle(targetHeight: number, releaseVelocity = 0) {
		settleDuration.value = getSettleDuration(targetHeight, releaseVelocity);
		activeGesture.value = null;
		settling.value = true;
		height.value = targetHeight || null;
		clearSettledHeight();
	}

	function getSettleDuration(targetHeight: number, releaseVelocity: number) {
		const currentHeight = height.value ?? targetHeight;
		const remainingDistance = Math.abs(targetHeight - currentHeight);
		const totalDistance = Math.max(expandedHeight - collapsedHeight, remainingDistance, 1);
		const distanceDuration = options.duration * Math.max(
			remainingDistance / totalDistance,
			MIN_SETTLE_DISTANCE_RATIO,
		);
		const momentumDuration = releaseVelocity > 0 ? remainingDistance / releaseVelocity : options.duration;

		return Math.round(Math.min(
			options.duration,
			Math.max(MIN_SETTLE_DURATION, Math.min(distanceDuration, momentumDuration)),
		));
	}

	return {
		dragging,
		events: { cancel, captureClick, clickCollapsed, end, move, start },
		settling: readonly(settling),
		style,
	};
}

function getDragHeights(surface: HTMLElement | null | undefined, collapsedElement: HTMLElement | null | undefined) {
	if (!surface || !collapsedElement) return { collapsedHeight: 0, expandedHeight: 0 };

	return {
		collapsedHeight: collapsedElement.getBoundingClientRect().height,
		expandedHeight: surface.getBoundingClientRect().height,
	};
}
